import React, { useState } from 'react';
import axios from 'axios';
import useConfirm from '../hooks/useConfirm';
import ConfirmModal from './ConfirmModal';

const UserProfile = ({ currentUser, setUser }) => {
    const [formData, setFormData] = useState({
        full_name: currentUser.full_name,
        email: currentUser.email,
        password: '', // Leave blank unless changing
        confirmPassword: '',
        role: currentUser.role
    });
    
    const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [verificationMessage, setVerificationMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { confirm, confirmState, handleClose } = useConfirm();

    const handleUpdate = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setVerificationMessage('');
        
        // Validate passwords match if password is provided
        if (formData.password && formData.password !== formData.confirmPassword) {
            alert("Passwords do not match.");
            setIsLoading(false);
            return;
        }
        
        // Check if ANY field has changed
        const nameChanged = formData.full_name !== currentUser.full_name;
        const emailChanged = formData.email !== currentUser.email;
        const passwordChanged = formData.password && formData.password.trim() !== '';
        const hasChanges = nameChanged || emailChanged || passwordChanged;
        
        if (!hasChanges) {
            alert("No changes to save.");
            setIsLoading(false);
            return;
        }
        
        // Request verification code
        try {
            await axios.post(
                `/api/users/${currentUser.user_id}/request-email-verification`,
                { 
                    newEmail: emailChanged ? formData.email : null,
                    currentEmail: currentUser.email
                }
            );
            setIsVerifyingEmail(true);
            setVerificationMessage('A verification code has been sent. Please check your email and enter the code below to confirm your changes.');
            setIsLoading(false);
        } catch (err) {
            setVerificationMessage('Error sending verification code. Please try again.');
            setIsLoading(false);
        }
    };
    
    const handleVerifyEmail = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setVerificationMessage('');
        
        const ok = await confirm({
            title: 'Confirm Profile Update',
            message: 'Confirm your profile changes?',
            confirmText: 'Save',
            cancelText: 'Cancel'
        });
        if (!ok) {
            setIsLoading(false);
            return;
        }
        
        try {
            await axios.post(
                `/api/users/${currentUser.user_id}/verify-email`,
                {
                    newEmail: formData.email !== currentUser.email ? formData.email : null,
                    verifyCurrent: currentUser.email,
                    verificationCode: verificationCode,
                    full_name: formData.full_name,
                    password: formData.password
                }
            );
            
            // Update local state and storage
            const updatedUser = { 
                ...currentUser, 
                full_name: formData.full_name, 
                email: formData.email 
            };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
            
            alert("Your profile has been updated successfully!");
            setIsVerifyingEmail(false);
            setVerificationCode('');
            setFormData({
                full_name: updatedUser.full_name,
                email: updatedUser.email,
                password: '',
                confirmPassword: '',
                role: updatedUser.role
            });
            setIsLoading(false);
        } catch (err) {
            setVerificationMessage(err.response?.data || "Failed to verify changes. Please try again.");
            setIsLoading(false);
        }
    };

    return (
        <div style={{ maxWidth: '400px', margin: '40px auto', padding: '20px', background: 'white', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
            <h3>My Profile Settings</h3>
            
            {!isVerifyingEmail ? (
                <form onSubmit={handleUpdate}>
                    <label style={labelStyle}>Full Name</label>
                    <input style={uInput} type="text" value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} />
                    
                    <label style={labelStyle}>Email Address</label>
                    <input style={uInput} type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                    
                    <label style={labelStyle}>New Password (leave blank to keep current)</label>
                    <input style={uInput} type="password" placeholder="••••••••" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} />
                    
                    <label style={labelStyle}>Confirm Password</label>
                    <input style={uInput} type="password" placeholder="••••••••" value={formData.confirmPassword} onChange={e => setFormData({...formData, confirmPassword: e.target.value})} />
                    
                    <button type="submit" style={saveBtn} disabled={isLoading}>
                        {isLoading ? 'Processing...' : 'Update My Details'}
                    </button>
                </form>
            ) : (
                <form onSubmit={handleVerifyEmail}>
                    <div style={verificationBox}>
                        <p style={{ color: '#666', marginBottom: '15px' }}>
                            {verificationMessage}
                        </p>
                        
                        <label style={labelStyle}>Verification Code</label>
                        <input 
                            style={uInput} 
                            type="text" 
                            placeholder="Enter 6-digit code"
                            maxLength="6"
                            value={verificationCode} 
                            onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                            required
                        />
                        
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button 
                                type="submit" 
                                style={saveBtn}
                                disabled={isLoading || verificationCode.length !== 6}
                            >
                                {isLoading ? 'Verifying...' : 'Verify Email'}
                            </button>
                            <button 
                                type="button" 
                                onClick={() => {
                                    setIsVerifyingEmail(false);
                                    setVerificationCode('');
                                    setVerificationMessage('');
                                }}
                                style={{ ...saveBtn, background: '#6c757d' }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </form>
            )}
            <ConfirmModal state={confirmState} onClose={handleClose} />
        </div>
    );
};

const labelStyle = { fontSize: '12px', color: '#666', marginBottom: '5px', display: 'block' };
const uInput = { width: '100%', padding: '10px', marginBottom: '15px', borderRadius: '4px', border: '1px solid #ddd' };
const saveBtn = { width: '100%', padding: '10px', background: '#800000', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' };
const verificationBox = { padding: '15px', background: '#f0f8ff', border: '2px solid #b91c1c', borderRadius: '6px', marginTop: '10px' };

export default UserProfile;

